import { useEffect, useRef } from "react";

// Sternschnuppen auf der Live-Karte: Jede QR-Weiterleitung fliegt als
// leuchtender Stern mit Schweif vom ungefaehren Standort des Aufrufs zum
// Unternehmen. Zeichnet direkt auf der Google-Karte (Polyline + Marker), damit
// Schweif und Kopf beim Verschieben und Zoomen korrekt mitlaufen.

const STAR_PATH = "M12 2l2.9 6 6.6.9-4.8 4.6 1.2 6.6L12 18.6 6.1 20.7l1.2-6.6L2.5 8.9 9.1 8z";
const MAX_PARALLEL = 4;
const LAUNCH_GAP_MS = 450;
const TAIL_SHARE = 0.24;
const TAIL_POINTS = 14;
const BURST_MS = 800;

const IS_DEBUG = process.env.REACT_APP_DEBUG === "true";
const debugLog = (tag, context) => {
  if (!IS_DEBUG) return;
  // eslint-disable-next-line no-console
  console.log(JSON.stringify({ level: "debug", tag: `home.map.${tag}`, ts: new Date().toISOString(), ...context }));
};

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const easeInOut = (t) => (t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2);

// Startpunkt und Bogen der Flugbahn in Kartenkoordinaten.
function buildPath(map, flight) {
  const to = flight.to;
  const bounds = map.getBounds();
  const north = bounds ? bounds.getNorthEast().lat() : to.lat + 0.1;
  const south = bounds ? bounds.getSouthWest().lat() : to.lat - 0.1;
  const east = bounds ? bounds.getNorthEast().lng() : to.lng + 0.1;
  const west = bounds ? bounds.getSouthWest().lng() : to.lng - 0.1;
  const latSpan = Math.max(0.01, north - south);
  const lngSpan = Math.max(0.01, east - west);

  // Ohne ermittelbaren Startort faellt der Stern vom oberen Kartenrand.
  let from = flight.from || { lat: north + latSpan * 0.05, lng: to.lng - lngSpan * 0.3 };

  // Liegt der Startort fast auf dem Ziel (gleiche Stadt), bekommt der Flug
  // trotzdem eine sichtbare Strecke von schraeg oben.
  const minLen = Math.min(latSpan, lngSpan) * 0.2;
  const dLat = to.lat - from.lat;
  const dLng = to.lng - from.lng;
  if (Math.hypot(dLat, dLng) < minLen) {
    from = { lat: to.lat + minLen * 0.8, lng: to.lng - minLen * 0.8 };
  }

  const mid = { lat: (from.lat + to.lat) / 2, lng: (from.lng + to.lng) / 2 };
  const len = Math.hypot(to.lat - from.lat, to.lng - from.lng);
  const control = {
    lat: mid.lat + ((to.lng - from.lng) / (len || 1)) * len * 0.22,
    lng: mid.lng - ((to.lat - from.lat) / (len || 1)) * len * 0.22,
  };
  return { from, control, to, length: len, viewSpan: Math.max(latSpan, lngSpan) };
}

function pointAt(path, t) {
  const u = 1 - t;
  return {
    lat: u * u * path.from.lat + 2 * u * t * path.control.lat + t * t * path.to.lat,
    lng: u * u * path.from.lng + 2 * u * t * path.control.lng + t * t * path.to.lng,
  };
}

const MapShootingStars = ({ map, flights, onArrive, onDone }) => {
  const startedRef = useRef(new Set());
  const queueRef = useRef([]);
  const activeRef = useRef(new Map());
  const lastLaunchRef = useRef(0);
  const timerRef = useRef(null);
  const callbacksRef = useRef({ onArrive, onDone });
  callbacksRef.current = { onArrive, onDone };

  useEffect(() => {
    if (!map || !window.google || !window.google.maps) return;
    flights.forEach((flight) => {
      if (startedRef.current.has(flight.id)) return;
      startedRef.current.add(flight.id);
      queueRef.current.push(flight);
    });
    pump();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, flights]);

  useEffect(() => () => {
    window.clearTimeout(timerRef.current);
    activeRef.current.forEach((anim) => anim.stop());
    activeRef.current.clear();
    queueRef.current = [];
  }, []);

  function finish(flight) {
    activeRef.current.delete(flight.id);
    if (callbacksRef.current.onDone) callbacksRef.current.onDone(flight.id);
    pump();
  }

  function pump() {
    window.clearTimeout(timerRef.current);
    if (!map || queueRef.current.length === 0 || activeRef.current.size >= MAX_PARALLEL) return;
    const wait = lastLaunchRef.current + LAUNCH_GAP_MS - Date.now();
    if (wait > 0) {
      timerRef.current = window.setTimeout(pump, wait);
      return;
    }
    const flight = queueRef.current.shift();
    lastLaunchRef.current = Date.now();
    launch(flight);
    if (queueRef.current.length > 0) timerRef.current = window.setTimeout(pump, LAUNCH_GAP_MS);
  }

  function launch(flight) {
    const { maps } = window.google;
    const arrive = () => {
      if (callbacksRef.current.onArrive) callbacksRef.current.onArrive(flight);
    };

    if (prefersReducedMotion() || document.hidden) {
      arrive();
      finish(flight);
      return;
    }

    const path = buildPath(map, flight);
    const duration = Math.round(1600 + Math.min(2200, (path.length / path.viewSpan) * 1800));
    debugLog("flight.start", { id: flight.id, hasOrigin: !!flight.from, duration });

    const tails = [
      { weight: 10, opacity: 0.18 },
      { weight: 5, opacity: 0.45 },
      { weight: 2, opacity: 0.95 },
    ].map(
      (style) =>
        new maps.Polyline({
          map,
          path: [],
          clickable: false,
          strokeColor: "#FBBC04",
          strokeOpacity: style.opacity,
          strokeWeight: style.weight,
          zIndex: 900,
        })
    );
    const head = new maps.Marker({
      map,
      position: path.from,
      clickable: false,
      optimized: false,
      zIndex: 1000,
      icon: {
        path: STAR_PATH,
        fillColor: "#FFD24D",
        fillOpacity: 1,
        strokeColor: "#ffffff",
        strokeWeight: 1.5,
        scale: 1.25,
        anchor: new maps.Point(12, 12),
      },
    });

    let frame = 0;
    let burst = null;
    const startedAt = performance.now();

    const cleanup = () => {
      window.cancelAnimationFrame(frame);
      tails.forEach((line) => line.setMap(null));
      head.setMap(null);
      if (burst) burst.setMap(null);
    };

    const runBurst = (from) => {
      burst = new maps.Marker({
        map,
        position: path.to,
        clickable: false,
        optimized: false,
        zIndex: 950,
        icon: { path: maps.SymbolPath.CIRCLE, scale: 4, fillColor: "#FBBC04", fillOpacity: 0.6, strokeColor: "#FBBC04", strokeOpacity: 0.9, strokeWeight: 2 },
      });
      const step = (now) => {
        const t = Math.min(1, (now - from) / BURST_MS);
        burst.setIcon({
          path: maps.SymbolPath.CIRCLE,
          scale: 4 + 22 * t,
          fillColor: "#FBBC04",
          fillOpacity: 0.6 * (1 - t),
          strokeColor: "#FBBC04",
          strokeOpacity: 0.9 * (1 - t),
          strokeWeight: 2,
        });
        if (t < 1) {
          frame = window.requestAnimationFrame(step);
        } else {
          cleanup();
          finish(flight);
        }
      };
      frame = window.requestAnimationFrame(step);
    };

    const step = (now) => {
      const raw = Math.min(1, (now - startedAt) / duration);
      const t = easeInOut(raw);
      const tailStart = Math.max(0, t - TAIL_SHARE);
      const points = [];
      for (let i = 0; i <= TAIL_POINTS; i++) {
        points.push(pointAt(path, tailStart + ((t - tailStart) * i) / TAIL_POINTS));
      }
      tails.forEach((line, index) => {
        // Aeussere Schweif-Schichten sind kuerzer, dadurch laeuft der Schweif spitz aus.
        const cut = Math.floor((points.length * (2 - index)) / 6);
        line.setPath(points.slice(cut));
      });
      head.setPosition(points[points.length - 1]);

      if (raw < 1) {
        frame = window.requestAnimationFrame(step);
        return;
      }
      tails.forEach((line) => line.setMap(null));
      head.setMap(null);
      arrive();
      runBurst(now);
    };

    activeRef.current.set(flight.id, { stop: cleanup });
    frame = window.requestAnimationFrame(step);
  }

  return null;
};

export default MapShootingStars;
